import React from 'react'
// components
import KPIResultCard from 'components/Modals/PlanItems/KPI/MinimizedModal/components/KPIResultsList/components/KPIResultCard'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import KPIResultTargetsModal from '../../../../../ResultTargetsModal'
// view
import { Wrapper } from 'components/Modals/PlanItems/KPI/MinimizedModal/components/KPIResultsList/components/KPIResultCard/view'
// constants
import { Forms, Modals, KPI_RESULT_TYPE_VALUES } from 'constants/index'
// context
import { KPIContext } from 'context/index'
// hooks
import { useShowModal } from 'hooks/index'
// utils
import moment from 'moment'
import get from 'lodash/get'
import { isUuid } from 'utils/uuid'
import { find, orderBy, pipe } from 'lodash/fp'
import { generateCapturedResults, generatePeriods, generateResultTarget, getFieldValues } from 'utils'
import { getScaleOptionValue } from 'utils/kpi'

const KPIResultsList: React.FC = () => {
  const { entity, update } = React.useContext(KPIContext) as any

  const { showModal: showConfirmModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_KPI_RESULT_MODAL })
  const { showModal: showSetResultModal, hideModal: hideSetResultModal } = useShowModal({
    modalType: Modals.KPI_SET_RESULT_ROW_MODAL
  })

  const isValueExpected = entity.resultType === KPI_RESULT_TYPE_VALUES.VALUE
  const capturedResults = get(entity, 'resultTarget.capturedResults', [])

  const { currentPeriods, todayPeriods, comingFirstPeriod, capturedResultsOutOfRange, ranges } = generatePeriods(entity)

  const periods: any[] = orderBy<any>(
    [({ startPoint }) => moment(startPoint).unix(), ({ lastDayOfThePeriod }) => moment(lastDayOfThePeriod).unix()],
    ['desc', 'desc']
  )([comingFirstPeriod, ...todayPeriods, ...capturedResultsOutOfRange, ...currentPeriods])

  const deleteResult = (period) => async () => {
    const captures = capturedResults.filter((item) => item.id !== period.id)
    const resultTarget = generateResultTarget(entity, captures, ranges)

    try {
      await update({
        variables: {
          id: entity.id,
          input: {
            fieldValues: getFieldValues(entity.fields, { resultTarget }),
            resultTarget
          }
        }
      })
    } catch (error) {
      console.log('[deleteResult]: ', error)
    }
  }

  const onDeleteResult = (period) => () =>
    showConfirmModal({
      width: 'sm',
      handleConfirm: deleteResult(period)
    })

  const handleShowSetResultModalSubmit = (period) => () => {
    const resultValue = pipe(
      find(
        ({ id, lastDayOfThePeriod }) =>
          id === period.id && moment(lastDayOfThePeriod).isSame(moment(period.lastDayOfThePeriod), 'day')
      ),
      (result: any) => {
        if (result) {
          const scaleValue = getScaleOptionValue(result.value, entity.resultType, entity.resultTypeScale)

          if (isValueExpected) {
            if (isUuid(result.value)) {
              return scaleValue.value
            }
            return result.value
          }

          return { label: scaleValue.label, value: scaleValue.id }
        }
      }
    )(capturedResults)

    showSetResultModal({
      initialValues: {
        resultValue
      },
      onSubmit: async (data) => {
        const captures = generateCapturedResults(capturedResults, period, isValueExpected, data.resultValue)
        const resultTarget = generateResultTarget(entity, captures, ranges)

        try {
          await update({
            variables: {
              id: entity.id,
              input: {
                fieldValues: getFieldValues(entity.fields, { resultTarget }),
                resultTarget
              }
            }
          })

          hideSetResultModal()
        } catch (error) {
          console.log('[handleShowSetResultModalSubmit]: ', error)
        }
      },
      entity
    })
  }

  return (
    <Wrapper>
      {periods.map((period) => (
        <KPIResultCard
          period={period}
          key={period.startPoint}
          deleteResult={onDeleteResult}
          showSetResultModal={handleShowSetResultModalSubmit}
        />
      ))}

      <KPIResultTargetsModal isResultsList targetForm={Forms.KPI_DETAILS_FORM} />
      <ConfirmDeleteModal modalType={Modals.CONFIRM_DELETE_KPI_RESULT_MODAL} header={`Delete Result`}>
        Are you sure you want to delete this result?
      </ConfirmDeleteModal>
    </Wrapper>
  )
}

export default KPIResultsList
